import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Select, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import MobileEmailPreview from '../MobileEmailPreview';

import './styles.scss';

const SenderForm = memo(
    ({
        selectInstanceKey = 0,
        senders,
        selectedSenderId,
        senderDisplayName,
        onSave,
        onCancel,
        onAddNewSender,
        onSelectedSenderChange,
    }) => {
        const theme = useTheme();

        const senderOptions = useMemo(() => senders, [senders]);

        const selectActions = useMemo(
            () => [
                {
                    iconType: 'addPlus',
                    label: getLiteral('action_add_a_new_sender'),
                    onClick: onAddNewSender,
                },
            ],
            [onAddNewSender],
        );

        const selectedSenderValue = useMemo(() => {
            if (!selectedSenderId) return null;
            return (
                senderOptions.find((opt) => String(opt.value) === String(selectedSenderId)) || null
            );
        }, [senderOptions, selectedSenderId]);

        const handleSenderChange = useCallback(
            (option) => {
                const raw =
                    option && typeof option === 'object' && 'value' in option
                        ? option.value
                        : option;
                if (raw === null || raw === undefined || raw === '') {
                    return;
                }
                if (typeof raw === 'object') {
                    const nested = raw.value ?? raw.id ?? raw.senderId;
                    if (nested === null || nested === undefined || nested === '') {
                        return;
                    }
                    onSelectedSenderChange?.(nested);
                    return;
                }
                onSelectedSenderChange?.(raw);
            },
            [onSelectedSenderChange],
        );

        const handleCancel = useCallback(() => {
            onCancel?.();
        }, [onCancel]);

        const handleSave = useCallback(() => {
            onSave?.({ senderId: selectedSenderId });
        }, [onSave, selectedSenderId]);

        const isValid = useMemo(() => Boolean(selectedSenderId), [selectedSenderId]);

        return (
            <div className="sender-form">
                <div className="sender-form__content">
                    <div className="sender-form__form">
                        <Select
                            key={selectInstanceKey}
                            label={getLiteral('label_email_address')}
                            isFullWidth
                            isRequired
                            isClearable={false}
                            options={senderOptions}
                            value={selectedSenderValue}
                            onChange={handleSenderChange}
                            placeholder={getLiteral(
                                'placeholder_marketing_campaigns_select_sender',
                            )}
                            actions={selectActions}
                        />
                        <Input
                            label={getLiteral('label_name')}
                            isFullWidth
                            isReadOnly
                            value={senderDisplayName}
                        />
                    </div>
                    <div className="sender-form__preview">
                        <MobileEmailPreview
                            title="Inbox"
                            name={senderDisplayName}
                            message="Message subject..."
                            previewText="Preview text"
                        />
                        <Text
                            type="caption"
                            color={theme.colors.textLight.secondary}
                            className="sender-form__preview-note"
                        >
                            {getLiteral('label_email_preview_may_vary')}
                        </Text>
                    </div>
                </div>
                <div className="sender-form__actions">
                    <Button type="secondary" size="small" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button type="primary" size="small" onClick={handleSave} isDisabled={!isValid}>
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

SenderForm.displayName = 'SenderForm';

SenderForm.propTypes = {
    selectInstanceKey: PropTypes.number,
    senders: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
            subLabel: PropTypes.string,
            isDefault: PropTypes.bool,
        }),
    ).isRequired,
    selectedSenderId: PropTypes.string,
    senderDisplayName: PropTypes.string,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onAddNewSender: PropTypes.func,
    onSelectedSenderChange: PropTypes.func,
};

export default SenderForm;
